/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as sharedTypes from 'ui-permit/src/constants';
import { readRuleMetadata } from 'ui-permit/src/utils/utilsNew';
import {
    DIVISION_ID_PROP_PATH,
    GROUP_DETAIL_ADDRESS1_PROP_PATH,
    GROUP_DETAIL_ADDRESS2_PROP_PATH,
    GROUP_DETAIL_ADDRESS_PROP_PATH,
    GROUP_DETAIL_ALT_PROP_PATH,
    GROUP_DETAIL_CITY_PROP_PATH,
    GROUP_DETAIL_EMAIL_PROP_PATH,
    GROUP_DETAIL_FIRST_NAME_PROP_PATH,
    GROUP_DETAIL_LAST_NAME_PROP_PATH,
    GROUP_DETAIL_MIDDLE_NAME_PROP_PATH,
    GROUP_DETAIL_PHONE_PROP_PATH,
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_STATE_PROP_PATH,
    GROUP_DETAIL_ZIPCODE_PROP_PATH,
    GROUP_INFO_PROP_PATH,
    GROUP_SIZE_PROP_PATH,
    LOTTERY_CHOICES_PROP_PATH,
    NEED_TO_KNOW_CHECKED_PROP_PATH,
    SELECTED_GROUP_SIZE_PROP_PATH,
    STATUS_PROP_PATH,
} from 'ui-permit/shared-components/constants/propPaths';

import { DNR_NUMBER_PROP_PATH } from './index';
import { permitTypes } from './hunting-permit-constants';

export const databindings = {
    [DIVISION_ID_PROP_PATH]: 'division_id',

    [GROUP_DETAIL_PROP_PATH]: 'permit_holder',
    [GROUP_DETAIL_ADDRESS_PROP_PATH]: 'permit_holder.home_address',
    [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
    [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
    [GROUP_DETAIL_MIDDLE_NAME_PROP_PATH]: 'middle_name',
    [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'zip_code',
    [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'address1',
    [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'address2',
    [GROUP_DETAIL_STATE_PROP_PATH]: 'state',
    [GROUP_DETAIL_CITY_PROP_PATH]: 'city',
    [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
    [GROUP_DETAIL_PHONE_PROP_PATH]: 'cell_phone',
    [GROUP_DETAIL_ALT_PROP_PATH]: 'alt_permit_holders',

    [GROUP_INFO_PROP_PATH]: 'group_members',

    [SELECTED_GROUP_SIZE_PROP_PATH]: 'extra_fields.group_size',
    [GROUP_SIZE_PROP_PATH]: 'extra_fields.group_size',
    [LOTTERY_CHOICES_PROP_PATH]: 'extra_fields.lottery_preferences',
    [DNR_NUMBER_PROP_PATH]: 'extra_fields.dnr_number',

    [NEED_TO_KNOW_CHECKED_PROP_PATH]: 'need_to_know_checked',

    [STATUS_PROP_PATH]: 'status',
};

export const lotteryPermitTypeMapping = {
    [sharedTypes.BLACKWATER_TURKEY_ID]: 'turkey-hunt',
    [sharedTypes.BOMBAY_HOOK_ID]: 'deer-hunt-lottery',
    [sharedTypes.PRIME_HOOK_ID]: 'deer-hunt-lottery',
    [sharedTypes.GEORGETOWN_LAKE_ID]: 'georgetown-lake-hunt',
    [sharedTypes.BELTON_LAKE_ID]: 'belton-lake-hunt',
    [sharedTypes.CAROLINA_SANDHILLS_ID]: 'carolina-sandhills-hunt',
    [sharedTypes.SOMERVILLE_LAKE_ID]: 'somerville-lake-hunt',
    [sharedTypes.GEORGETOWN_WATERFOWL_ID]: 'georgetown-waterfowl-hunt',
    [sharedTypes.GEORGETOWN_OTHER_ID]: 'georgetown-other-hunt',
    [sharedTypes.MATTAMUSKEET_NWR_WATERFOWL]: 'mattamuskeet-nwr-waterfowl-hunt',
    [sharedTypes.MATTAMUSKEET_DISABLED_WATERFOWL]: 'mattamuskeet-disabled-waterfowl-hunt',
};

export function readMaxGroupSizeRule(permitID, permitType, rules) {
    const maxGroupSizeRuleMetadata =
        readRuleMetadata(sharedTypes.MAX_GROUP_SIZE, rules) || '{}';
    const metadataObj = JSON.parse(maxGroupSizeRuleMetadata);
    return metadataObj[permitType] || maxGroupSizeMapping[permitID]?.[permitType] || 1;
}

export const maxGroupSizeMapping = Object.freeze({
    [sharedTypes.BLACKWATER_ID]: {
        [permitTypes.MUZZLE_LOADER_LIMITED]: 1,
        [permitTypes.SHOTGUN_LIMITED]: 1,
        [permitTypes.MUZZLE_LOADER_OPEN_EARLY]: 1,
        [permitTypes.MUZZLE_LOADER_OPEN_LATE]: 1,
        [permitTypes.YOUTH_HUNT]: 1,
        [permitTypes.SHOTGUN_OPEN_EARLY]: 1,
        [permitTypes.SHOTGUN_OPEN_LATE]: 1,
        [permitTypes.ARCHERY]: 1,
        [permitTypes.PRIMITIVE]: 1,
        [permitTypes.SPORTSMANS_PACKAGE]: 1,
    },
    [sharedTypes.EASTERN_NECK_ID]: {
        [permitTypes.ARCHERY]: 1,
        [permitTypes.MUZZLE_LOADER_OPEN]: 1,
        [permitTypes.SHOTGUN_OPEN]: 1,
        [permitTypes.SHOTGUN_OPEN_EARLY]: 1,
        [permitTypes.SHOTGUN_OPEN_LATE]: 1,
        [permitTypes.DISABLED_HUNT]: 1,
        [permitTypes.YOUTH_HUNT]: 1,
        [permitTypes.PRIMITIVE]: 1,
    },
    [sharedTypes.BLACKWATER_TURKEY_ID]: {
        [permitTypes.YOUTH_HUNT]: 1,
        [permitTypes.DISABLED_HUNT]: 1,
        [permitTypes.TURKEY_HUNT]: 1,
    },
    [sharedTypes.BLACKWATER_WATERFOWL_ID]: {
        [permitTypes.WATERFOWL_HUNT]: 1,
    },
    [sharedTypes.BOMBAY_HOOK_ID]: {
        [permitTypes.DEER_HUNT_LOTTERY]: 1,
    },
    [sharedTypes.PRIME_HOOK_ID]: {
        [permitTypes.DEER_HUNT_LOTTERY]: 1,
    },
    [sharedTypes.LAKE_LANIER_ID]: {
        [permitTypes.LAKE_LANIER_HUNT]: 1,
    },
    [sharedTypes.CAROLINA_SANDHILLS_ID]: {
        [permitTypes.CAROLINA_SANDHILLS_HUNT]: 3,
    },
    [sharedTypes.WALTER_F_GEORGE_LAKE_ID]: {
        [permitTypes.WALTER_F_GEORGE_LAKE]: 1,
    },
    [sharedTypes.TENNESSEE_TOMBIGBEE_WATERWAY_ID]: {
        [permitTypes.TENNESSEE_TOMBIGBEE_WATERWAY_HUNT]: 1,
    },
    [sharedTypes.PIEDMONT_NWR_DEER_ID]: {
        [permitTypes.PIEDMONT_NWR_PRIMITIVE_HUNT]: 1,
        [permitTypes.PIEDMONT_NWR_FIREARMS_HUNT_1]: 1,
        [permitTypes.PIEDMONT_NWR_FIREARMS_HUNT_2]: 1,
    },
    [sharedTypes.LAS_VEGAS_NWR_DEER_ID]: {
        [permitTypes.LAS_VEGAS_NWR_GOOSE_HUNT]: 3,
    },
    [sharedTypes.PIEDMONT_NWR_TURKEY_ID]: {
        [permitTypes.PIEDMONT_NWR_TURKEY_HUNT_1]: 1,
        [permitTypes.PIEDMONT_NWR_TURKEY_HUNT_2]: 1,
        [permitTypes.PIEDMONT_NWR_TURKEY_HUNT_3]: 1,
    },
    [sharedTypes.SOMERVILLE_LAKE_ID]: {
        [permitTypes.SOMERVILLE_LAKE_HUNT]: 7,
    },
    [sharedTypes.HAVASU_NWR_ID]: {
        [permitTypes.HAVASU_WATERFOWL_HUNT]: 1,
    },
    [sharedTypes.WHITNEY_AQUILLA_LAKES]: {
        [permitTypes.WHITNEY_AQUILLA_LAKES_HUNT]: 1,
    },
    [sharedTypes.MATTAMUSKEET_NWR_WATERFOWL]: {
        [permitTypes.MATTAMUSKEET_NWR_WATERFOWL_HUNT]: 3,
    },
    [sharedTypes.MATTAMUSKEET_DISABLED_WATERFOWL]: {
        [permitTypes.MATTAMUSKEET_DISABLED_WATERFOWL_HUNT]: 3,
    },
    [sharedTypes.MATTAMUSKEET_YOUTH_WATERFOWL]: {
        [permitTypes.MATTAMUSKEET_YOUTH_WATERFOWL_HUNT_1]: 4,
        [permitTypes.MATTAMUSKEET_YOUTH_WATERFOWL_HUNT_2]: 4,
    },
});

export const maxPreferredDatesMapping = {
    // 1 Per Permit Date
    [sharedTypes.BOMBAY_HOOK_ID]: 3,
    [sharedTypes.PRIME_HOOK_ID]: 2,
};

export const vehiclesSizeMapping = Object.freeze({
    [sharedTypes.TENNESSEE_TOMBIGBEE_WATERWAY_ID]: {
        min: 0,
        max: 1,
    },
    [sharedTypes.SOMERVILLE_LAKE_ID]: {
        min: 1,
        max: 7,
    },
    [sharedTypes.WHITNEY_AQUILLA_LAKES]: {
        min: 1,
        max: 1,
    },
    [sharedTypes.MATTAMUSKEET_NWR_WATERFOWL]: {
        min: 1,
        max: 1,
    },
    [sharedTypes.MATTAMUSKEET_DISABLED_WATERFOWL]: {
        min: 1,
        max: 1,
    },
    [sharedTypes.MATTAMUSKEET_YOUTH_WATERFOWL]: {
        min: 1,
        max: 1,
    },
});
